/**
 * Shared roadmap display metadata — labels, option lists and badge classes used
 * by both the grid and the editor modal so the two can't drift.
 */
import type {
  RoadmapOwner,
  RoadmapPriority,
  RoadmapScope,
  RoadmapStatus,
} from '../../types';

export const STATUS_OPTIONS: RoadmapStatus[] = [
  'active',
  'backlog',
  'shipped',
  'dropped',
];

export const PRIORITY_OPTIONS: RoadmapPriority[] = ['P0', 'P1', 'P2', 'P3'];

export const OWNER_OPTIONS: RoadmapOwner[] = ['james', 'orchestrator', 'both'];

export const SCOPE_OPTIONS: RoadmapScope[] = ['S', 'M', 'L', 'XL'];

export const STATUS_LABELS: Record<RoadmapStatus, string> = {
  active: 'Active',
  backlog: 'Backlog',
  shipped: 'Shipped',
  dropped: 'Dropped',
};

export const OWNER_LABELS: Record<RoadmapOwner, string> = {
  james: 'James',
  orchestrator: 'Orchestrator',
  both: 'Both',
};

export const STATUS_BADGE: Record<RoadmapStatus, string> = {
  active: 'bg-emerald-100 text-emerald-800',
  backlog: 'bg-gray-100 text-gray-700',
  shipped: 'bg-blue-100 text-blue-800',
  dropped: 'bg-gray-100 text-gray-400 line-through',
};

/** P0 loudest → P3 quietest. */
export const PRIORITY_BADGE: Record<RoadmapPriority, string> = {
  P0: 'bg-red-100 text-red-800',
  P1: 'bg-amber-100 text-amber-800',
  P2: 'bg-sky-100 text-sky-800',
  P3: 'bg-gray-100 text-gray-600',
};

/** Neutral throughout — scope is a size, not a severity, so it shouldn't shout. */
export const SCOPE_BADGE: Record<RoadmapScope, string> = {
  S: 'bg-gray-100 text-gray-600',
  M: 'bg-gray-100 text-gray-600',
  L: 'bg-gray-200 text-gray-700',
  XL: 'bg-gray-200 text-gray-700',
};

/**
 * Column order for the kanban board. Deliberately not `STATUS_OPTIONS` even
 * though the two currently match: filter-chip order and board order are free to
 * diverge, and the board wants Active first.
 */
export const KANBAN_COLUMNS: RoadmapStatus[] = [
  'active',
  'backlog',
  'shipped',
  'dropped',
];

/**
 * Rank orders for the enum fields. Shared by the grid's column sort and the
 * kanban's within-column sort so the two can't disagree on what "P0 first"
 * means.
 */
export const PRIORITY_RANK: Record<RoadmapPriority, number> = {
  P0: 0,
  P1: 1,
  P2: 2,
  P3: 3,
};

export const STATUS_RANK: Record<RoadmapStatus, number> = {
  active: 0,
  backlog: 1,
  shipped: 2,
  dropped: 3,
};

export const SCOPE_RANK: Record<string, number> = { S: 0, M: 1, L: 2, XL: 3 };

/** Unranked / missing values sort to the end regardless of direction. */
export const UNRANKED = 99;

/**
 * Fall back to a neutral badge when a doc carries a status/priority outside the
 * current enum (an older seed, or a value added server-side before the pin was
 * bumped) rather than rendering an undefined class string.
 */
export function statusBadgeClass(status: RoadmapStatus | undefined): string {
  if (!status) return 'bg-gray-100 text-gray-500';
  return STATUS_BADGE[status] ?? 'bg-gray-100 text-gray-500';
}

export function statusLabel(status: RoadmapStatus | undefined): string {
  if (!status) return '—';
  return STATUS_LABELS[status] ?? status;
}

export function priorityBadgeClass(
  priority: RoadmapPriority | undefined,
): string {
  if (!priority) return 'bg-gray-100 text-gray-500';
  return PRIORITY_BADGE[priority] ?? 'bg-gray-100 text-gray-500';
}

export function ownerLabel(owner: RoadmapOwner | undefined): string {
  if (!owner) return '—';
  return OWNER_LABELS[owner] ?? owner;
}

export function scopeBadgeClass(scope: RoadmapScope | null | undefined): string {
  if (!scope) return 'bg-gray-100 text-gray-500';
  return SCOPE_BADGE[scope] ?? 'bg-gray-100 text-gray-500';
}

/** Rank helpers that tolerate missing / off-enum values. */
export function priorityRank(priority: RoadmapPriority | undefined): number {
  if (!priority) return UNRANKED;
  return PRIORITY_RANK[priority] ?? UNRANKED;
}

export function statusRank(status: RoadmapStatus | undefined): number {
  if (!status) return UNRANKED;
  return STATUS_RANK[status] ?? UNRANKED;
}

export function scopeRank(scope: RoadmapScope | null | undefined): number {
  if (!scope) return UNRANKED;
  return SCOPE_RANK[scope] ?? UNRANKED;
}

/** 'a, b , ,c' → ['a','b','c'] — trimmed, de-duped, empties dropped. */
export function parseTags(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(',')) {
    const tag = part.trim();
    if (!tag || seen.has(tag)) continue;
    seen.add(tag);
    out.push(tag);
  }
  return out;
}
